import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, Check, Headphones, Languages, Sparkles, Play, FileText, Pause } from "lucide-react";
import { Link } from "react-router-dom";

export function LandingPage() {
  const [previewStep, setPreviewStep] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [previewHovered, setPreviewHovered] = useState(false);
  const [previewFocused, setPreviewFocused] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const previewSteps = [
    { label: "Source file", type: "Upload", icon: FileText, tone: "blue", copy: "Sunday message.mp3" },
    { label: "Transcript", type: "Captured", icon: FileText, tone: "green", copy: "Your message, ready to review" },
    { label: "Translation", type: "Spanish", icon: Languages, tone: "purple", copy: "A clear version for more listeners" },
    { label: "Voiceover", type: "Chris", icon: Headphones, tone: "amber", copy: "Charming, Down-to-Earth" },
  ];
  const currentPreview = previewSteps[previewStep];

  useEffect(() => {
    if (previewHovered || previewFocused) return;
    const timer = window.setInterval(() => {
      setPreviewStep((step) => (step + 1) % previewSteps.length);
    }, 3600);
    return () => window.clearInterval(timer);
  }, [previewFocused, previewHovered, previewSteps.length]);

  const selectPreviewStep = (step: number) => {
    if (step !== 3 && audioRef.current) {
      audioRef.current.pause();
    }
    setPreviewStep(step);
  };

  const togglePreviewAudio = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) audio.pause();
    else void audio.play().catch(() => setPlaying(false));
  };

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="flex-1">
              <h1 className="text-4xl md:text-6xl font-bold leading-tight">
                Transcribe the Word, Proclaim the Message with
                <span className="bg-gradient-to-r from-purple-600 to-yellow-500 text-transparent bg-clip-text">
                  {" "}Divine Voice
                </span>
              </h1>
              <p className="text-lg md:text-xl text-gray-600 mt-6 max-w-xl">
                Translate biblical and theological content into multiple languages and create 
                natural-sounding audio with our advanced AI technology.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg">
                  Try for Free
                </button>
                <Link to="/demo" className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-800 flex items-center">
                  <Play className="mr-2 h-4 w-4" /> Watch Demo
                </Link>
              </div>
              <div className="flex items-center gap-6 mt-10">
                <div className="flex -space-x-4">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center border-2 border-white">
                      <span className="text-xs font-medium">U{i}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-1">
                    <span className="text-sm font-medium">4.9</span>
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <svg key={i} className="h-4 w-4 fill-yellow-500" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">from 500+ reviews</p>
                </div>
              </div>
            </div>
            <div className="flex-1 min-w-0 w-full relative">
              <div className="absolute -top-10 -left-10 h-64 w-64 rounded-full bg-blue-200 filter blur-3xl animate-pulse"></div>
              <div className="relative min-w-0 w-full">
                <div className="landing-preview-card bg-white border border-gray-200 rounded-xl p-8 backdrop-blur-sm shadow-xl" onMouseEnter={() => setPreviewHovered(true)} onMouseLeave={() => setPreviewHovered(false)} onFocusCapture={() => setPreviewFocused(true)} onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setPreviewFocused(false); }}>
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-2">
                      <div className="h-2.5 w-2.5 bg-emerald-500 rounded-full animate-pulse"></div>
                      <span className="text-sm font-semibold">Live project preview</span>
                    </div>
                    <span className="text-[10px] font-semibold uppercase tracking-[.14em] text-gray-400">Sunday message</span>
                  </div>
                  <div className="mb-5 flex items-center gap-1 overflow-x-auto pb-1">
                    {previewSteps.map((step, index) => {
                      const Icon = step.icon;
                      return <React.Fragment key={step.label}>
                        {index > 0 && <ArrowRight className="mx-1 shrink-0 text-gray-300" size={14} />}
                        <button type="button" onClick={() => selectPreviewStep(index)} className={`flex shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-2 text-left text-[10px] font-semibold transition ${previewStep === index ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-gray-200 bg-gray-50 text-gray-500 hover:bg-gray-100"}`}>
                          <Icon size={13} />{step.label}{index < previewStep && <Check size={12} className="text-emerald-600" />}
                        </button>
                      </React.Fragment>;
                    })}
                  </div>
                  <div className={`landing-preview-stage h-[236px] overflow-hidden rounded-xl border p-5 ${currentPreview.tone === "blue" ? "border-blue-100 bg-blue-50" : currentPreview.tone === "green" ? "border-emerald-100 bg-emerald-50" : currentPreview.tone === "purple" ? "border-purple-100 bg-purple-50" : "border-amber-100 bg-amber-50"}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div><p className="text-[10px] font-bold uppercase tracking-[.14em] text-gray-500">Step {previewStep + 1} of {previewSteps.length} · {currentPreview.type}</p><h3 className="mt-2 text-lg font-semibold text-gray-800">{currentPreview.label}</h3><p className="mt-1 text-sm text-gray-600">{currentPreview.copy}</p></div>
                      <span className="rounded-full bg-white/80 px-2 py-1 text-[10px] font-semibold text-emerald-700">Ready</span>
                    </div>
                    {previewStep === 3 && <div className="landing-preview-voice mt-5 rounded-lg border border-white/80 bg-white/75 p-3"><div className="flex items-center justify-between gap-2 text-xs font-semibold text-gray-700"><span>Chris - Charming, Down-to-Earth</span><button type="button" onClick={togglePreviewAudio} className="flex shrink-0 items-center gap-1 text-emerald-700">{playing ? <Pause size={13} /> : <Play size={13} />} {playing ? "Pause" : "Play sample"}</button></div><div className="mt-3 flex h-7 items-center gap-1">{[34,58,42,74,50,86,38,65,47,78,55,70,43,82,51,68,39,61,49,76,44,57,72,46].map((height, i) => <span key={i} className="w-1 rounded-full bg-amber-400" style={{ height: `${height}%` }} />)}</div><audio ref={audioRef} src="/demo-chris.mp3" className="sr-only" onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)} /></div>}
                  </div>
                  <div className="mt-4 flex items-center justify-between"><span className="text-[11px] text-gray-400">{previewHovered || previewFocused ? "Paused · move away to continue" : "Auto-playing preview"}</span><button type="button" onClick={() => selectPreviewStep((previewStep + 1) % previewSteps.length)} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-900">Next step <ArrowRight size={13} /></button></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="py-20 px-6 md:px-12 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold">Powerful Features for Sacred Content</h2>
            <p className="text-lg text-gray-600 mt-4 max-w-2xl mx-auto">
              Everything you need to translate and vocalize your biblical and theological text
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
              <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                <Languages className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Multilingual Translation</h3>
              <p className="text-gray-600">
                Translate sacred texts into over 100 languages with contextual understanding of theological terms.
              </p>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
              <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                <Headphones className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Natural Voice Generation</h3>
              <p className="text-gray-600">
                Convert text to speech with natural-sounding voices that respect the reverence of sacred content.
              </p>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
              <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                <Sparkles className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Theological Accuracy</h3>
              <p className="text-gray-600">
                AI trained on theological texts ensures the accuracy and context of important religious concepts.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* How It Works Section */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold">How Divine Echo Works</h2>
            <p className="text-lg text-gray-600 mt-4 max-w-2xl mx-auto">
              A simple three-step process to transform your sacred texts
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6 relative">
                <FileText className="h-8 w-8 text-blue-600" />
                <div className="absolute h-8 w-8 rounded-full border-2 border-blue-600 bg-transparent animate-ping"></div>
              </div>
              <h3 className="text-xl font-semibold mb-3">1. Upload Text</h3>
              <p className="text-gray-600">
                Upload or paste your biblical or theological content into our secure platform.
              </p>
            </div>
            
            <div className="text-center">
              <div className="h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6 relative">
                <Languages className="h-8 w-8 text-blue-600" />
                <div className="absolute h-8 w-8 rounded-full border-2 border-blue-600 bg-transparent animate-ping"></div>
              </div>
              <h3 className="text-xl font-semibold mb-3">2. Select Languages</h3>
              <p className="text-gray-600">
                Choose from over 100 languages for translation and select your preferred voice.
              </p>
            </div>
            
            <div className="text-center">
              <div className="h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6 relative">
                <Headphones className="h-8 w-8 text-blue-600" />
                <div className="absolute h-8 w-8 rounded-full border-2 border-blue-600 bg-transparent animate-ping"></div>
              </div>
              <h3 className="text-xl font-semibold mb-3">3. Listen & Download</h3>
              <p className="text-gray-600">
                Preview, edit if needed, and download your translated text and audio files.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Testimonials Section */}
      <section className="py-20 px-6 md:px-12 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold">Trusted by Faith Leaders Worldwide</h2>
            <p className="text-lg text-gray-600 mt-4 max-w-2xl mx-auto">
              See what pastors, theologians, and religious educators are saying about Divine Echo
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
              <div className="flex items-center gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <svg key={i} className="h-5 w-5 fill-yellow-500" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-gray-600 mb-6">
                "Divine Echo has transformed our global ministry. Now our sermons reach believers in their native languages with natural voices that convey the emotion behind our message."
              </p>
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="font-semibold">PR</span>
                </div>
                <div>
                  <p className="font-medium">Pastor Robert Johnson</p>
                  <p className="text-sm text-gray-600">Global Faith Ministries</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
              <div className="flex items-center gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <svg key={i} className="h-5 w-5 fill-yellow-500" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-gray-600 mb-6">
                "The accuracy of theological terms in translation is impressive. Divine Echo understands context and nuance in ways other services simply cannot match."
              </p>
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="font-semibold">DL</span>
                </div>
                <div>
                  <p className="font-medium">Dr. Linda Williams</p>
                  <p className="text-sm text-gray-600">Theological Seminary</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
              <div className="flex items-center gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <svg key={i} className="h-5 w-5 fill-yellow-500" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-gray-600 mb-6">
                "For our church with members from diverse backgrounds, Divine Echo has been a blessing. Now everyone can listen to our teachings in their preferred language."
              </p>
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="font-semibold">FM</span>
                </div>
                <div>
                  <p className="font-medium">Father Michael Chen</p>
                  <p className="text-sm text-gray-600">Grace Community Church</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-12 text-white text-center">
          <h2 className="text-3xl md:text-4xl font-bold">Begin Your Divine Echo Journey Today</h2>
          <p className="text-lg mt-4 max-w-xl mx-auto opacity-90">
            Join thousands of faith leaders who are reaching more people through the power of multilingual voice technology.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mt-8 justify-center">
            <button className="px-4 py-2 bg-white text-purple-800 hover:bg-white/90 rounded-lg">
              Get Started for Free
            </button>
            <button className="px-4 py-2 border border-white text-white hover:bg-white/10 rounded-lg">
              Schedule a Demo
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
